import type { MenuItemWithDetails, ShopInfo, Weekday } from './twin';

/**
 * Build a schema.org `CafeOrCoffeeShop` JSON-LD object, grounded in the same shop
 * info + live menu the page renders. Because this is a static client-rendered SPA
 * (no SSR), we inject this into <head> at runtime from one source of truth rather
 * than hand-maintaining a duplicate block in index.html. See README.md.
 */

const DAY_SCHEMA: Record<Weekday, string> = {
  0: 'https://schema.org/Sunday',
  1: 'https://schema.org/Monday',
  2: 'https://schema.org/Tuesday',
  3: 'https://schema.org/Wednesday',
  4: 'https://schema.org/Thursday',
  5: 'https://schema.org/Friday',
  6: 'https://schema.org/Saturday',
};

export function buildLocalBusinessJsonLd(
  shop: ShopInfo,
  featured: MenuItemWithDetails[],
): Record<string, unknown> {
  const openingHours = (Object.keys(shop.hours) as unknown as Weekday[])
    .map((day) => {
      const window = shop.hours[day];
      if (!window) return null;
      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: DAY_SCHEMA[day],
        opens: window.open,
        closes: window.close,
      };
    })
    .filter(Boolean);

  return {
    '@context': 'https://schema.org',
    '@type': 'CafeOrCoffeeShop',
    name: shop.name,
    description: shop.tagline,
    address: {
      '@type': 'PostalAddress',
      streetAddress: shop.address_line,
      addressLocality: shop.city,
      addressRegion: shop.region,
      postalCode: shop.postal_code,
      addressCountry: shop.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: shop.geo.latitude,
      longitude: shop.geo.longitude,
    },
    telephone: shop.phone,
    priceRange: shop.price_range,
    servesCuisine: 'Coffee',
    openingHoursSpecification: openingHours,
    hasMenu: {
      '@type': 'Menu',
      hasMenuItem: featured.map((item) => ({
        '@type': 'MenuItem',
        name: item.name,
        description: item.description,
        offers: {
          '@type': 'Offer',
          price: (item.price_cents / 100).toFixed(2),
          priceCurrency: 'USD',
        },
      })),
    },
  };
}
